import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Stethoscope, Building2, ShieldCheck, Ambulance, Pill, Smile, UserRound, Wrench, Phone, Mail, Pencil, Trash2, Plus, X, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'medico',        label: 'Médico / Pediatra', icon: Stethoscope, color: 'bg-blue-100 text-blue-800 dark:bg-blue-400/20 dark:text-blue-300 dark:ring-1 dark:ring-blue-400/40' },
  { key: 'obra_social',   label: 'Obra social',       icon: Building2,   color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-400/40' },
  { key: 'seguro_medico', label: 'Seguro médico',     icon: ShieldCheck, color: 'bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-400/40' },
  { key: 'ambulancia',    label: 'Ambulancia',        icon: Ambulance,   color: 'bg-red-100 text-red-700 dark:bg-red-500/25 dark:text-red-300 dark:ring-1 dark:ring-red-400/40' },
  { key: 'farmacia',      label: 'Farmacia',          icon: Pill,        color: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/25 dark:text-fuchsia-300 dark:ring-1 dark:ring-fuchsia-400/40' },
  { key: 'odontologo',    label: 'Odontólogo',        icon: Smile,       color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-400/20 dark:text-cyan-300 dark:ring-1 dark:ring-cyan-400/40' },
  { key: 'familiar',      label: 'Familiar cercano',  icon: UserRound,   color: 'bg-pink-100 text-pink-800 dark:bg-pink-400/20 dark:text-pink-300 dark:ring-1 dark:ring-pink-400/40' },
  { key: 'otro',          label: 'Otro',              icon: Wrench,      color: 'bg-slate-200 text-slate-700 dark:bg-slate-500/25 dark:text-slate-300 dark:ring-1 dark:ring-slate-400/30' },
];
const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
const emptyDraft = { name: '', category: 'medico', phone: '', email: '', notes: '', belongs_to: '' };

export default function EmergencyContactsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/emergency-contacts`, { withCredentials: true });
      setItems(data);
    } catch {
      toast.error('Error al cargar los contactos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      if (filter !== 'all' && c.category !== filter) return false;
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.notes || '').toLowerCase().includes(q) ||
        (c.belongs_to || '').toLowerCase().includes(q)
      );
    });
  }, [items, filter, query]);

  const openNew = () => { setDraft(emptyDraft); setEditingId(null); setShowForm(true); };
  const openEdit = (item) => {
    setDraft({
      name: item.name || '', category: item.category, phone: item.phone || '',
      email: item.email || '', notes: item.notes || '', belongs_to: item.belongs_to || '',
    });
    setEditingId(item.id); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setDraft(emptyDraft); };

  const save = async (e) => {
    e.preventDefault();
    if (draft.name.trim().length < 2) { toast.error('El nombre es obligatorio'); return; }
    if (!draft.phone && !draft.email) { toast.error('Cargá al menos un teléfono o email'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/api/emergency-contacts/${editingId}`, draft, { withCredentials: true });
        toast.success('Contacto actualizado');
      } else {
        await axios.post(`${API}/api/emergency-contacts`, draft, { withCredentials: true });
        toast.success('Contacto guardado');
      }
      closeForm(); load();
    } catch {
      toast.error('No pudimos guardar el contacto');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.name}?`)) return;
    try {
      await axios.delete(`${API}/api/emergency-contacts/${item.id}`, { withCredentials: true });
      toast.success('Contacto eliminado'); load();
    } catch { toast.error('No pudimos eliminar el contacto'); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="emergency-contacts-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
            Emergencias
          </h1>
          <p className="text-muted-foreground">Todos tus contactos importantes al alcance de un toque</p>
        </div>
        <button
          data-testid="new-contact-btn"
          onClick={openNew}
          className="px-5 py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all shadow-md hover:-translate-y-0.5 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuevo contacto
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-2xl shadow-md p-4 sm:p-5 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="contact-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, teléfono o nota..."
            className="w-full pl-10 pr-4 py-2 bg-surface-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            data-testid="cat-filter-all"
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors ${filter === 'all' ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30' : 'bg-muted text-muted-foreground hover:bg-surface-2'}`}
          >
            Todos
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`cat-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 flex items-center gap-1.5 transition-colors ${filter === c.key ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30' : c.color}`}
            >
              <c.icon className="w-3.5 h-3.5" strokeWidth={2} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <form
          data-testid="contact-form"
          onSubmit={save}
          className="bg-surface rounded-2xl shadow-lg p-6 sm:p-8 space-y-4 border-2 border-[#D97706]/30"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-foreground">{editingId ? 'Editar contacto' : 'Nuevo contacto'}</h2>
            <button type="button" onClick={closeForm} className="p-2 hover:bg-muted rounded-full text-muted-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Nombre</label>
              <input
                data-testid="contact-name-input"
                type="text" required
                value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: Dra. López"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Categoría</label>
              <select
                data-testid="contact-category-select"
                value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              >
                {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Teléfono</label>
              <input
                data-testid="contact-phone-input"
                type="tel" value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="+54 11 5555 5555"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Email</label>
              <input
                data-testid="contact-email-input"
                type="email" value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="contacto@ejemplo.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Pertenece a</label>
              <input
                data-testid="contact-belongsto-input"
                type="text" value={draft.belongs_to}
                onChange={(e) => setDraft({ ...draft, belongs_to: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: Sofía, Toda la familia"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Notas</label>
              <textarea
                data-testid="contact-notes-input"
                rows={2} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706] resize-none"
                placeholder="Nº de afiliado, dirección, horario, especialidad..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeForm} className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-full">Cancelar</button>
            <button
              type="submit" disabled={saving} data-testid="contact-save-btn"
              className="px-6 py-2 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <div className="bg-surface rounded-2xl shadow-md p-10 text-center">
          <UserRound className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground">
            {items.length === 0 ? 'Todavía no cargaste contactos de emergencia' : 'No hay contactos que coincidan con el filtro'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const cat = CATEGORY_MAP[c.category] || CATEGORY_MAP.otro;
            const Icon = cat.icon;
            return (
              <div
                key={c.id}
                data-testid={`contact-${c.id}`}
                className="bg-surface rounded-2xl shadow-md p-5 border border-slate-100 flex flex-col"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cat.color}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-foreground truncate">{c.name}</h3>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">{cat.label}</p>
                    {c.belongs_to && <p className="text-xs text-muted-foreground mt-1">Para: {c.belongs_to}</p>}
                  </div>
                </div>

                {(c.phone || c.email) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                    {c.phone && (
                      <a
                        href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`}
                        data-testid={`contact-call-${c.id}`}
                        className="flex items-center gap-2 text-sm text-foreground font-semibold hover:text-primary"
                      >
                        <Phone className="w-4 h-4" strokeWidth={2} /> {c.phone}
                      </a>
                    )}
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        data-testid={`contact-email-${c.id}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground break-all"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> {c.email}
                      </a>
                    )}
                  </div>
                )}

                {c.notes && (
                  <p className="mt-3 pt-3 border-t border-slate-100 text-xs text-muted-foreground whitespace-pre-line">{c.notes}</p>
                )}

                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => openEdit(c)}
                    data-testid={`contact-edit-${c.id}`}
                    title="Editar" className="p-2 hover:bg-muted rounded-full text-muted-foreground"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(c)}
                    data-testid={`contact-delete-${c.id}`}
                    title="Eliminar" className="p-2 hover:bg-red-50 rounded-full text-red-600 ml-auto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
